import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/api/client'
import type { AxiosError } from 'axios'
import { useWalletStore } from '@/stores/wallet'
import type { WalletBalance } from '@/stores/wallet'

export interface Game {
  id: number
  slug: string
  title: string
  category: string
  rtp: number | null
  thumbnail_url: string | null
  badges: string[]
}

export interface RecentWin {
  username: string
  bet_amount: number
  win_amount: number
  multiplier: number
  created_at: string
}

export interface GameDetail {
  id: number
  slug: string
  title: string
  category: string
  rtp: number | null
  description: string | null
  rules_url: string | null
  thumbnail_url: string | null
  is_hot: boolean
  is_new: boolean
  badges: string[]
  play_count: number
  min_bet: number
  max_bet: number
  recent_wins: RecentWin[]
}

export interface PlayResult {
  payout: number
  multiplier: number
  is_win: boolean
  outcome_data: Record<string, unknown>
  game_round_id: number
  balance: {
    bonus_used: number
    cash_used: number
  }
}

export interface GamesMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

export type SortOption = 'popular' | 'newest' | 'a-z' | 'rtp'

export const useGamesStore = defineStore('games', () => {
  const games = ref<Game[]>([])
  const meta = ref<GamesMeta>({ current_page: 1, last_page: 1, per_page: 25, total: 0 })
  const loading = ref(false)
  const loadingMore = ref(false)
  const error = ref<string | null>(null)

  const activeCategory = ref<string | null>(null)
  const activeSort = ref<SortOption>('popular')
  const searchQuery = ref('')

  const hasMore = computed(() => meta.value.current_page < meta.value.last_page)

  // Game detail state
  const gameDetail = ref<GameDetail | null>(null)
  const loadingDetail = ref(false)
  const detailError = ref<string | null>(null)

  async function fetchGames(page = 1, append = false) {
    if (page === 1) {
      loading.value = true
    } else {
      loadingMore.value = true
    }
    error.value = null

    try {
      const params: Record<string, string | number> = {
        page,
        limit: 25,
        sort: activeSort.value,
      }

      if (activeCategory.value) {
        params.category = activeCategory.value
      }

      if (searchQuery.value.trim()) {
        params.q = searchQuery.value.trim()
      }

      const response = await apiClient.get('/api/games', { params })
      const data = response.data

      if (append) {
        games.value = [...games.value, ...data.data]
      } else {
        games.value = data.data
      }

      meta.value = data.meta
    } catch {
      error.value = 'Failed to load games'
    } finally {
      loading.value = false
      loadingMore.value = false
    }
  }

  function setCategory(category: string | null) {
    activeCategory.value = category
    fetchGames(1)
  }

  function setSort(sort: SortOption) {
    activeSort.value = sort
    fetchGames(1)
  }

  function setSearch(query: string) {
    searchQuery.value = query
    fetchGames(1)
  }

  function loadMore() {
    if (hasMore.value && !loadingMore.value) {
      fetchGames(meta.value.current_page + 1, true)
    }
  }

  async function fetchGameDetail(slug: string) {
    loadingDetail.value = true
    detailError.value = null
    gameDetail.value = null

    try {
      const response = await apiClient.get(`/api/games/${slug}`)
      gameDetail.value = response.data
    } catch {
      detailError.value = 'Failed to load game details'
    } finally {
      loadingDetail.value = false
    }
  }

  // Favorites state
  const favoriteGameIds = ref<Set<number>>(new Set())
  const favoritesLoaded = ref(false)

  async function fetchFavorites() {
    try {
      const response = await apiClient.get('/api/favorites')
      favoriteGameIds.value = new Set(response.data.game_ids as number[])
      favoritesLoaded.value = true
    } catch {
      // Silently fail — favorites are non-critical
    }
  }

  function isFavorited(gameId: number): boolean {
    return favoriteGameIds.value.has(gameId)
  }

  async function toggleFavorite(gameId: number): Promise<boolean> {
    try {
      const response = await apiClient.post(`/api/favorites/${gameId}/toggle`)
      const isFav = response.data.is_favorited as boolean
      if (isFav) {
        favoriteGameIds.value.add(gameId)
      } else {
        favoriteGameIds.value.delete(gameId)
      }
      // Trigger reactivity
      favoriteGameIds.value = new Set(favoriteGameIds.value)
      return isFav
    } catch {
      return favoriteGameIds.value.has(gameId)
    }
  }

  // Play state
  const playing = ref(false)
  const playError = ref<string | null>(null)
  const rateLimited = ref(false)
  const rateLimitRetryAfter = ref(0)
  let rateLimitTimer: ReturnType<typeof setTimeout> | null = null

  async function playGame(
    slug: string,
    amount: number,
    options: Record<string, unknown> = {},
  ): Promise<PlayResult | null> {
    if (rateLimited.value) {
      playError.value = 'Too many bets — please wait before placing another bet.'
      return null
    }

    playing.value = true
    playError.value = null

    // Snapshot balance before optimistic debit so we can roll back on failure.
    const walletStore = useWalletStore()
    const previousBalance: WalletBalance | null = walletStore.balance ? { ...walletStore.balance } : null
    if (previousBalance) {
      walletStore.applyGameResult(amount, 0) // optimistic debit
    }

    try {
      const response = await apiClient.post(`/api/games/${slug}/play`, { amount: amount.toFixed(2), options })
      return response.data as PlayResult
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string }>

      // Restore balance to pre-debit state on any API failure.
      if (previousBalance) {
        walletStore.rollbackBalance(previousBalance)
      }

      if (axiosErr.response?.status === 429) {
        const retryAfter = parseInt(
          (axiosErr.response.headers['retry-after'] as string) || '1',
          10,
        )
        rateLimited.value = true
        rateLimitRetryAfter.value = retryAfter
        playError.value = 'Too many bets — please wait before placing another bet.'

        if (rateLimitTimer) clearTimeout(rateLimitTimer)
        rateLimitTimer = setTimeout(() => {
          rateLimited.value = false
          rateLimitRetryAfter.value = 0
          playError.value = null
        }, retryAfter * 1000)
      } else {
        playError.value = axiosErr.response?.data?.message || 'Failed to place bet'
      }

      return null
    } finally {
      playing.value = false
    }
  }

  return {
    games,
    meta,
    loading,
    loadingMore,
    error,
    activeCategory,
    activeSort,
    searchQuery,
    hasMore,
    fetchGames,
    setCategory,
    setSort,
    setSearch,
    loadMore,
    gameDetail,
    loadingDetail,
    detailError,
    fetchGameDetail,
    favoriteGameIds,
    favoritesLoaded,
    fetchFavorites,
    isFavorited,
    toggleFavorite,
    playing,
    playError,
    rateLimited,
    rateLimitRetryAfter,
    playGame,
  }
})
